import React from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Table from 'components/application/Table';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import CourseTraining from 'components/training/CourseTraining';

import { mappedAutoEnrolmentExclusionsByConfigurationId } from 'components/helpers/resources/autoEnrolments';
import { getELearningCourseId } from 'components/helpers/resources/courses';
import { groupTrainingByCourseId } from 'components/helpers/resources/training';
import { resourceShape } from 'components/helpers/serialisableResources';

export default function AdditionalTrainingTable({
  attachments,
  autoEnrolmentConfigurations,
  autoEnrolmentExclusions,
  bookers,
  coursesWithTrainingStatusInfo,
  creditInfoUnavailable,
  currentBookings,
  currentPersonnel,
  currentRegistrations,
  eLearningAllowance,
  eLearningCourses,
  hasTrainingEditableAccess,
  onAddBooking,
  onAddELearningBooking,
  onBookingReminderClick,
  onCreateAutoEnrolmentExclusion,
  onDeleteAutoEnrolmentExclusion,
  onDeleteCourseClick,
  onDeleteTrainingClick,
  onEditTrainingClick,
  onNewTrainingClick,
  onRemoveBooking,
  onShowBooking,
  onUserCourseCreate,
  onUserCourseUpdate,
  personnelId,
  setModalBoxData,
  training,
}) {
  const { user: currentUser } = useCurrentActor();

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const trainingByCourseId = groupTrainingByCourseId({
    training,
  });

  const courseTrainingCollections = (
    coursesWithTrainingStatusInfo || []
  ).reduce((acc, courseData) => {
    const {
      course,
      personnelCourseConnectionRequirementPriority,
      personnelCourseConnectionRequirementPriorityUser,
      requiredSource,
      trainingStatus,
      userCourse,
    } = courseData;
    const eLearningCourse = eLearningCourses[getELearningCourseId(course)];
    const autoEnrolmentConfiguration =
      autoEnrolmentConfigurations &&
      autoEnrolmentConfigurations[
        course.relationships?.autoEnrolmentConfiguration?.data?.id
      ];
    const autoEnrolmentExclusion =
      autoEnrolmentExclusions &&
      mappedAutoEnrolmentExclusionsByConfigurationId(autoEnrolmentExclusions)[
        autoEnrolmentConfiguration?.id
      ];
    const training = trainingByCourseId[course.id];
    const booking = currentBookings.find(
      (booking) => booking.relationships.course.data.id === course.id,
    );
    const booker = bookers && bookers[booking?.relationships?.booker?.data?.id];
    const registration = currentRegistrations.find(
      (registration) => registration.relationships.course.data.id === course.id,
    );

    const orderingBand = courseData?.orderingBand;
    const isRequired = courseData?.isRequired;
    const isAssignedToAnotherRole = courseData?.isAssignedToAnotherRole;
    const isOptionalOnAnotherRole = courseData?.isOptionalOnAnotherRole;
    const isRequiredByAnything = isRequired || isAssignedToAnotherRole;

    const component = (
      <CourseTraining
        attachments={attachments}
        autoEnrolmentConfiguration={autoEnrolmentConfiguration}
        autoEnrolmentExclusion={autoEnrolmentExclusion}
        booker={booker}
        booking={booking}
        course={course}
        creditInfoUnavailable={creditInfoUnavailable}
        currentPersonnel={currentPersonnel}
        eLearningAllowance={eLearningAllowance}
        eLearningCourse={eLearningCourse}
        hasTrainingEditableAccess={hasTrainingEditableAccess}
        isAdditional={true}
        isAssignedToAnotherRole={isAssignedToAnotherRole}
        isOptionalOnAnotherRole={isOptionalOnAnotherRole}
        isRequired={isRequired}
        isRequiredByAnything={isRequiredByAnything}
        isWithinRoleTable={false}
        key={`additionalCourseTraining--${course.id}`}
        onAddBooking={onAddBooking}
        onAddELearningBooking={onAddELearningBooking}
        onBookingReminderClick={onBookingReminderClick}
        onCreateAutoEnrolmentExclusion={onCreateAutoEnrolmentExclusion}
        onDeleteAutoEnrolmentExclusion={onDeleteAutoEnrolmentExclusion}
        onDeleteCourseClick={onDeleteCourseClick}
        onDeleteTrainingClick={onDeleteTrainingClick}
        onEditTrainingClick={onEditTrainingClick}
        onNewTrainingClick={onNewTrainingClick}
        onRemoveBooking={onRemoveBooking}
        onShowBooking={onShowBooking}
        onUserCourseCreate={onUserCourseCreate}
        onUserCourseUpdate={onUserCourseUpdate}
        personnelCourseConnectionRequirementPriority={
          personnelCourseConnectionRequirementPriority
        }
        personnelCourseConnectionRequirementPriorityUser={
          personnelCourseConnectionRequirementPriorityUser
        }
        personnelId={personnelId}
        registration={registration}
        requiredSource={requiredSource}
        setModalBoxData={setModalBoxData}
        training={training}
        trainingStatus={trainingStatus}
        userCourse={userCourse}
      />
    );

    const row = {
      priorityStatus: orderingBand,
      name: course.attributes.name,
      component,
    };
    const insertIndex = acc.findIndex(
      (obj) =>
        (obj.priorityStatus === orderingBand &&
          obj.name > course.attributes.name) ||
        obj.priorityStatus > orderingBand,
    );

    insertIndex === -1 ? acc.push(row) : acc.splice(insertIndex, 0, row);

    return acc;
  }, []);

  return (
    <Table
      headers={
        <tr>
          <th className='tw-w-16.5 tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900' />
          <th className='tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900'>
            {i18nTShared('name')}
          </th>
          <th className='tw-w-31 tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900'>
            {i18nTShared('expiryDate')}
          </th>
          <th className='tw-w-54 tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900'>
            {i18nTShared('bookings')}
          </th>
          {currentUser.attributes.accessType !== 'personnel' && (
            <th className='tw-w-18 tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900'>
              {i18nTShared('actions')}
            </th>
          )}
        </tr>
      }
      rows={courseTrainingCollections.map((row) => row.component)}
    />
  );
}

AdditionalTrainingTable.propTypes = {
  eLearningCourses: PropTypes.objectOf(resourceShape('eLearningCourse')),
  training: PropTypes.arrayOf(resourceShape('training')),
  bookers: PropTypes.objectOf(resourceShape('simpleUser')),
  autoEnrolmentExclusions: PropTypes.object,
  eLearningAllowance: PropTypes.object,
  currentBookings: PropTypes.arrayOf(resourceShape('booking')),
  currentRegistrations: PropTypes.array,
  creditInfoUnavailable: PropTypes.bool,
  onBookingReminderClick: PropTypes.func.isRequired,
  onShowBooking: PropTypes.func.isRequired,
  onRemoveBooking: PropTypes.func.isRequired,
  onCreateAutoEnrolmentExclusion: PropTypes.func,
  onDeleteAutoEnrolmentExclusion: PropTypes.func,
  coursesWithTrainingStatusInfo: PropTypes.arrayOf(
    PropTypes.shape({
      course: resourceShape('course'),
      isRequired: PropTypes.bool,
      isAssignedToAnotherRole: PropTypes.bool,
      isOptionalOnAnotherRole: PropTypes.bool,
      latestTraining: resourceShape('training'),
      orderingBand: PropTypes.number,
      requiredSource: PropTypes.string,
      trainingStatus: PropTypes.string,
      userCourse: resourceShape('userCourse'),
    }),
  ),
  autoEnrolmentConfigurations: PropTypes.object,
  hasTrainingEditableAccess: PropTypes.bool.isRequired,
  attachments: PropTypes.array,
  currentPersonnel: PropTypes.object.isRequired,
  onUserCourseUpdate: PropTypes.func.isRequired,
  onUserCourseCreate: PropTypes.func.isRequired,
  onNewTrainingClick: PropTypes.func.isRequired,
  onEditTrainingClick: PropTypes.func.isRequired,
  onDeleteTrainingClick: PropTypes.func.isRequired,
  onDeleteCourseClick: PropTypes.func.isRequired,
  onAddBooking: PropTypes.func.isRequired,
  onAddELearningBooking: PropTypes.func.isRequired,
  setModalBoxData: PropTypes.func,
  personnelId: PropTypes.node,
};

AdditionalTrainingTable.defaultProps = {
  training: [],
};
